import { format, addDays, parseISO, differenceInDays } from "date-fns";
import { getDisconnectScheduleDateStr, getScheduleDatePart } from "../modules/disconnectSchedules/utils/dateUtils";

/**
 * Print utility functions for disconnect schedules
 */

/**
 * Organizes groups by remote/non-remote status
 * Works with new schedule-based structure: transforms schedules into propertyGroups format
 * @param {Object} dayData - Day data with schedules
 * @returns {Object} Object with remote and nonRemote arrays
 */
export const organizeGroupsByRemoteStatus = (dayData) => {
    // Handle both old structure (propertyGroups) and new structure (schedules)
    if (!dayData) {
        return { remote: [], nonRemote: [] };
    }

    // If old structure, use existing logic
    if (dayData.propertyGroups) {
        const remoteGroups = [];
        const nonRemoteGroups = [];

        Object.values(dayData.propertyGroups).forEach((propertyGroup) => {
            const remoteProperties = {};
            const nonRemoteProperties = {};

            Object.values(propertyGroup.properties || {}).forEach((property) => {
                // Handle both old structure (disconnects) and transformed structure (accounts)
                const items = property.disconnects || property.accounts || [];
                if (items.length === 0) return;

                // Determine if property is remote based on property.isRemote or items
                let isPropertyRemote = property.isRemote === 1 || property.isRemote === true;

                // If property doesn't have isRemote flag, check items
                if (property.isRemote === undefined && items.length > 0) {
                    // Check first item to determine remote status
                    const firstItem = items[0];
                    isPropertyRemote = firstItem.isRemote === 1 || firstItem.isRemote === true;
                }

                if (isPropertyRemote) {
                    remoteProperties[property.propertyUuid] = property;
                } else {
                    nonRemoteProperties[property.propertyUuid] = property;
                }
            });

            if (Object.keys(remoteProperties).length > 0) {
                remoteGroups.push({
                    ...propertyGroup,
                    properties: remoteProperties
                });
            }

            if (Object.keys(nonRemoteProperties).length > 0) {
                nonRemoteGroups.push({
                    ...propertyGroup,
                    properties: nonRemoteProperties
                });
            }
        });

        return { remote: remoteGroups, nonRemote: nonRemoteGroups };
    }

    // New structure: work with schedules
    if (!dayData.schedules) {
        return { remote: [], nonRemote: [] };
    }

    // Aggregate schedules by propertyGroup
    const groupsMap = new Map();

    Object.values(dayData.schedules).forEach((schedule) => {
        const propertyGroup = schedule.propertyGroup;
        if (!propertyGroup) return;

        const groupUuid = propertyGroup.uuid;
        if (!groupsMap.has(groupUuid)) {
            groupsMap.set(groupUuid, {
                groupUuid: propertyGroup.uuid,
                groupName: propertyGroup.name,
                groupId: propertyGroup.id,
                properties: {}
            });
        }

        const group = groupsMap.get(groupUuid);

        // Aggregate properties from this schedule
        Object.entries(schedule.properties || {}).forEach(([propertyUuid, property]) => {
            if (!property.propertyInfo) return;

            const isRemote = property.propertyInfo.isRemote === 1 || property.propertyInfo.isRemote === true;

            if (!group.properties[propertyUuid]) {
                group.properties[propertyUuid] = {
                    propertyUuid: property.propertyInfo.uuid,
                    propertyName: property.propertyInfo.name,
                    isRemote: isRemote,
                    accounts: []
                };
            }

            // Add accounts from this schedule
            if (property.accounts && property.accounts.length > 0) {
                group.properties[propertyUuid].accounts.push(...property.accounts);
            }
        });
    });

    // Separate into remote and non-remote groups
    const remoteGroups = [];
    const nonRemoteGroups = [];

    groupsMap.forEach((group) => {
        const remoteProperties = {};
        const nonRemoteProperties = {};

        Object.entries(group.properties).forEach(([propertyUuid, property]) => {
            if (property.isRemote) {
                remoteProperties[propertyUuid] = property;
            } else {
                nonRemoteProperties[propertyUuid] = property;
            }
        });

        // Check if group has any properties to determine remote status
        const hasRemoteProperties = Object.keys(remoteProperties).length > 0;
        const hasNonRemoteProperties = Object.keys(nonRemoteProperties).length > 0;

        // If group has remote properties, add to remote groups
        if (hasRemoteProperties) {
            remoteGroups.push({
                ...group,
                properties: remoteProperties
            });
        }

        // If group has non-remote properties, add to non-remote groups
        if (hasNonRemoteProperties) {
            nonRemoteGroups.push({
                ...group,
                properties: nonRemoteProperties
            });
        }

        // If group has no properties at all, check all schedules for this group to infer remote status
        if (!hasRemoteProperties && !hasNonRemoteProperties) {
            // Check all schedules for this group to see if we can determine remote status
            let hasAnyRemoteProperties = false;
            let hasAnyNonRemoteProperties = false;

            Object.values(dayData.schedules || {}).forEach((schedule) => {
                if (schedule.propertyGroup?.uuid === group.groupUuid) {
                    Object.values(schedule.properties || {}).forEach((property) => {
                        if (property.propertyInfo) {
                            const isRemote = property.propertyInfo.isRemote === 1 || property.propertyInfo.isRemote === true;
                            if (isRemote) {
                                hasAnyRemoteProperties = true;
                            } else {
                                hasAnyNonRemoteProperties = true;
                            }
                        }
                    });
                }
            });

            // If we found remote properties in any schedule, add to remote groups only
            if (hasAnyRemoteProperties) {
                remoteGroups.push({
                    ...group,
                    properties: {}
                });
            } else {
                // If we found non-remote properties or couldn't determine, add to non-remote groups
                nonRemoteGroups.push({
                    ...group,
                    properties: {}
                });
            }
        }
    });

    return { remote: remoteGroups, nonRemote: nonRemoteGroups };
};

/**
 * Filter accounts based on qualifying criteria for the disconnect swimlane.
 * Aligned with batch-dailyprocess.js rules:
 * (1) Only Active accounts,
 * (2) Completed workflows closed during final notice (last letter final) are excluded,
 * (3) On the disconnect-letter stage (mostRecentNotice === 'disconnect'), column visibility follows
 *     letterDisconnect.sendDate when set (reconnect paths clear disconnected=1),
 * (4) Final notice sent at least 2 days ago (in-progress lane).
 * (5) Exclude accounts whose most recent letter is 10-day or 14-day (earlier stages only).
 *
 * Accounts eligible for reconnect (paid enough, winter crisis, med form, etc.)
 * are kept visible so the UI can show a "Reconnect" badge.
 *
 * @param {Array} accounts - Array of account objects
 * @param {string} [targetDateStr] - Calendar column date (YYYY-MM-DD). Defaults to today UTC.
 * @returns {Array} Filtered accounts array
 */
export const filterQualifyingAccounts = (accounts, targetDateStr) => {
    if (!accounts || !Array.isArray(accounts)) {
        return [];
    }

    const localToday = format(new Date(), 'yyyy-MM-dd');

    const columnDateStr =
        targetDateStr && typeof targetDateStr === 'string'
            ? targetDateStr.substring(0, 10)
            : localToday;

    const todayStr = localToday;

    return accounts.filter((account) => {
        if (account.AccountStatus !== 'Active') {
          return false;
        }
        const mostRecent = account.notices?.mostRecentNotice;
        // Swimlane starts at final notice onward — not 10-day / 14-day stages
        if (mostRecent === '10-day' || mostRecent === '14-day') {
            return false;
        }
        // Process closed while last letter was final notice — never show in swimlane
        if (account.completed && mostRecent === 'final') {
            return false;
        }
        // Disconnect-letter stage: qualify without relying on disconnected=1 (reconnect clears it).
        if (mostRecent === 'disconnect') {
            if (account.completed) {
                return columnDateStr <= todayStr;
            }
            return true;
        }
        if (account.disconnected) {
            return true;
        }
        if (account.completed) {
            return false;
        }

        const finalNotice = account.notices?.letterFinalNotice;
        if (!finalNotice || !finalNotice.sendDate) {
            return false;
        }

        const noticeDateStr = getScheduleDatePart(finalNotice.sendDate);
        if (!noticeDateStr) return false;
        const noticeDate = new Date(noticeDateStr + "T00:00:00");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return differenceInDays(today, noticeDate) >= 2;
    });
};

/**
 * Get accounts for a property that match the target date
 * Works with both old structure (disconnects) and new structure (accounts)
 * @param {Object} property - Property object with disconnects or accounts array
 * @param {string} targetDateStr - Target date string in YYYY-MM-DD format
 * @returns {Array} Filtered accounts/disconnects array
 */
export const getDisconnectsForDate = (property, targetDateStr) => {
    if (!property) {
        return [];
    }

    const targetNorm =
        targetDateStr && typeof targetDateStr === 'string'
            ? targetDateStr.substring(0, 10)
            : targetDateStr;

    // Old structure: property has disconnects array
    if (property.disconnects) {
        return property.disconnects.filter((disconnect) => {
            const mostRecent = disconnect.notices?.mostRecentNotice;
            const letterPart = getScheduleDatePart(
                disconnect?.notices?.letterDisconnect?.sendDate
            );
            if (mostRecent === 'disconnect') {
                if (letterPart) return letterPart === targetNorm;
                const disconnectDateStr = disconnect.disconnectDate
                    ? getDisconnectScheduleDateStr(disconnect.disconnectDate)
                    : null;
                if (disconnectDateStr) return disconnectDateStr === targetNorm;
                return true;
            }
            if (!disconnect.disconnectDate) return false;
            const disconnectDateStr = getDisconnectScheduleDateStr(disconnect.disconnectDate);
            return disconnectDateStr === targetNorm;
        });
    }

    // New structure: property has accounts array
    // Final-notice (etc.) rows: same as before — not disconnected => include; disconnected =>
    // match disconnectDate. Disconnect-letter stage: pin to letter send date, not disconnected=1.
    if (property.accounts) {
        return property.accounts.filter((account) => {
            const mostRecent = account.notices?.mostRecentNotice;
            const letterPart = getScheduleDatePart(
                account?.notices?.letterDisconnect?.sendDate
            );
            if (mostRecent === 'disconnect') {
                if (letterPart) return letterPart === targetNorm;
                const disconnectDateStr = account.disconnectDate
                    ? getDisconnectScheduleDateStr(account.disconnectDate)
                    : null;
                if (disconnectDateStr) return disconnectDateStr === targetNorm;
                return true;
            }
            if (!account.disconnected) return true;
            if (!account.disconnectDate) return false;
            const disconnectDateStr = getDisconnectScheduleDateStr(account.disconnectDate);
            return disconnectDateStr === targetNorm;
        });
    }

    return [];
};

/**
 * Get available groups for printing (from selected day or entire week)
 * Works with both old structure (propertyGroups) and new structure (schedules)
 * @param {Object} dataToUse - Week data object with data property
 * @param {string|null} selectedDate - Selected date key or null for entire week
 * @returns {Array} Array of available groups
 */
export const getAvailableGroupsForPrint = (dataToUse, selectedDate) => {
    const groupsMap = new Map();

    const processDayData = (dayData) => {
        // Old structure
        if (dayData.propertyGroups) {
            Object.values(dayData.propertyGroups).forEach((group) => {
                groupsMap.set(group.groupUuid, {
                    groupUuid: group.groupUuid,
                    groupName: group.groupName,
                    groupId: group.groupId
                });
            });
        }
        // New structure: extract unique propertyGroups from schedules
        else if (dayData.schedules) {
            Object.values(dayData.schedules).forEach((schedule) => {
                const propertyGroup = schedule.propertyGroup;
                if (propertyGroup && propertyGroup.uuid) {
                    groupsMap.set(propertyGroup.uuid, {
                        groupUuid: propertyGroup.uuid,
                        groupName: propertyGroup.name,
                        groupId: propertyGroup.id
                    });
                }
            });
        }
    };

    if (selectedDate && dataToUse.data[selectedDate]) {
        processDayData(dataToUse.data[selectedDate]);
    } else {
        Object.values(dataToUse.data).forEach((dayData) => {
            processDayData(dayData);
        });
    }

    return Array.from(groupsMap.values());
};

/**
 * Filter data based on selected groups and type
 * Works with both old structure (propertyGroups) and new structure (schedules)
 * @param {Object} dataToUse - Week data object with data property
 * @param {string|null} selectedDate - Selected date key or null for entire week
 * @param {Array} selectedGroups - Array of selected group UUIDs
 * @param {string} selectedType - Type filter: 'remote', 'nonRemote', or 'both'
 * @returns {Object} Filtered data object (transformed to propertyGroups format for compatibility)
 */
export const filterDataForPrint = (dataToUse, selectedDate, selectedGroups, selectedType) => {
    let dataToFilter;

    if (selectedDate) {
        // Try to find matching date key - handle both exact match and date string variations
        let matchingDateKey = null;

        // First try exact match
        if (dataToUse.data[selectedDate]) {
            matchingDateKey = selectedDate;
        } else {
            // Try to find by comparing date strings (handle ISO strings)
            Object.keys(dataToUse.data).forEach((key) => {
                const dayData = dataToUse.data[key];
                if (dayData) {
                    const dateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
                        ? dayData.date.split('T')[0]
                        : dayData.date;
                    if (dateStr === selectedDate || key === selectedDate) {
                        matchingDateKey = key;
                    }
                }
            });
        }

        if (matchingDateKey && dataToUse.data[matchingDateKey]) {
            dataToFilter = { [matchingDateKey]: dataToUse.data[matchingDateKey] };
        } else {
            // No matching date found, return empty filtered data
            return {};
        }
    } else {
        dataToFilter = dataToUse.data;
    }

    const filteredData = {};

    Object.entries(dataToFilter).forEach(([dateKey, dayData]) => {
        const targetDateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
            ? dayData.date.split('T')[0]
            : dayData.date || dateKey;

        // Old structure
        if (dayData.propertyGroups) {
            const filteredGroups = {};

            Object.entries(dayData.propertyGroups).forEach(([groupUuid, group]) => {
                if (selectedGroups.length > 0 && !selectedGroups.includes(groupUuid)) {
                    return;
                }

                const filteredProperties = {};

                Object.entries(group.properties || {}).forEach(([propertyUuid, property]) => {
                    if (!property.disconnects) return;

                    let filteredDisconnects = property.disconnects;

                    if (selectedType === 'remote') {
                        filteredDisconnects = filteredDisconnects.filter(
                            d => d.isRemote === 1 || d.isRemote === true
                        );
                    } else if (selectedType === 'nonRemote') {
                        filteredDisconnects = filteredDisconnects.filter(
                            d => d.isRemote === 0 || d.isRemote === false || d.isRemote == null
                        );
                    }

                    // Filter by qualifying criteria (Active + final notice sent at least 4 days ago)
                    // Convert disconnects to accounts format for filtering
                    const accounts = filteredDisconnects.map(d => ({
                        ...d,
                        AccountStatus: d.AccountStatus || d.accountStatus,
                        notices: d.notices || {}
                    }));
                    const qualifyingAccounts = filterQualifyingAccounts(accounts, targetDateStr);

                    if (qualifyingAccounts.length > 0) {
                        filteredProperties[propertyUuid] = {
                            ...property,
                            disconnects: qualifyingAccounts
                        };
                    }
                });

                if (Object.keys(filteredProperties).length > 0) {
                    filteredGroups[groupUuid] = {
                        ...group,
                        properties: filteredProperties
                    };
                }
            });

            if (Object.keys(filteredGroups).length > 0) {
                filteredData[dateKey] = {
                    ...dayData,
                    propertyGroups: filteredGroups
                };
            }
        }
        // New structure: filter schedules and transform to propertyGroups format
        else if (dayData.schedules) {
            // targetDateStr already set at top of forEach from dayData.date

            const filteredSchedules = {};

            Object.entries(dayData.schedules).forEach(([scheduleId, schedule]) => {
                const propertyGroup = schedule.propertyGroup;
                if (!propertyGroup) return;

                // Filter by selected groups
                if (selectedGroups.length > 0 && !selectedGroups.includes(propertyGroup.uuid)) {
                    return;
                }

                // Filter schedules by target date - only include schedules matching the target date
                if (selectedDate && schedule.scheduleInfo?.scheduledDisconnectDate) {
                    const scheduleDateStr = typeof schedule.scheduleInfo.scheduledDisconnectDate === 'string' && schedule.scheduleInfo.scheduledDisconnectDate.includes('T')
                        ? schedule.scheduleInfo.scheduledDisconnectDate.split('T')[0]
                        : schedule.scheduleInfo.scheduledDisconnectDate;

                    // Only include schedules that match the target date
                    if (scheduleDateStr !== targetDateStr) {
                        return;
                    }
                }

                const filteredProperties = {};

                Object.entries(schedule.properties || {}).forEach(([propertyUuid, property]) => {
                    if (!property.propertyInfo || !property.accounts) return;

                    const isRemote = property.propertyInfo.isRemote === 1 || property.propertyInfo.isRemote === true;

                    // Filter by remote status
                    if (selectedType === 'remote' && !isRemote) return;
                    if (selectedType === 'nonRemote' && isRemote) return;

                    // Filter accounts by qualifying criteria (processed + pre-disconnect queue)
                    const qualifyingAccounts = filterQualifyingAccounts(property.accounts, targetDateStr);

                    if (qualifyingAccounts.length > 0) {
                        filteredProperties[propertyUuid] = {
                            propertyUuid: property.propertyInfo.uuid,
                            propertyName: property.propertyInfo.name,
                            isRemote: isRemote,
                            accounts: qualifyingAccounts
                        };
                    }
                });

                if (Object.keys(filteredProperties).length > 0) {
                    filteredSchedules[scheduleId] = {
                        ...schedule,
                        properties: filteredProperties
                    };
                }
            });

            // Transform schedules to propertyGroups format for print compatibility
            if (Object.keys(filteredSchedules).length > 0) {
                const groupsMap = new Map();

                Object.values(filteredSchedules).forEach((schedule) => {
                    const propertyGroup = schedule.propertyGroup;
                    const groupUuid = propertyGroup.uuid;

                    if (!groupsMap.has(groupUuid)) {
                        groupsMap.set(groupUuid, {
                            groupUuid: propertyGroup.uuid,
                            groupName: propertyGroup.name,
                            groupId: propertyGroup.id,
                            properties: {}
                        });
                    }

                    const group = groupsMap.get(groupUuid);
                    // Merge properties from this schedule into the group
                    // If a property already exists, merge the accounts arrays
                    Object.entries(schedule.properties || {}).forEach(([propertyUuid, property]) => {
                        if (group.properties[propertyUuid]) {
                            // Property already exists, merge accounts arrays
                            const existingProperty = group.properties[propertyUuid];
                            const existingAccounts = existingProperty.accounts || [];
                            const newAccounts = property.accounts || [];
                            // Combine and deduplicate accounts by accountId or accountUuid
                            const accountsMap = new Map();
                            existingAccounts.forEach(acc => {
                                const key = acc.accountId || acc.accountUuid || acc.accountDisconnectId;
                                if (key) accountsMap.set(key, acc);
                            });
                            newAccounts.forEach(acc => {
                                const key = acc.accountId || acc.accountUuid || acc.accountDisconnectId;
                                if (key) accountsMap.set(key, acc);
                            });
                            // Preserve isRemote flag (should be the same for same property)
                            group.properties[propertyUuid] = {
                                ...existingProperty,
                                accounts: Array.from(accountsMap.values()),
                                isRemote: existingProperty.isRemote !== undefined ? existingProperty.isRemote : property.isRemote
                            };
                        } else {
                            // New property, add it directly (preserve isRemote flag)
                            group.properties[propertyUuid] = {
                                ...property,
                                isRemote: property.isRemote !== undefined ? property.isRemote : false
                            };
                        }
                    });
                });

                const filteredGroups = {};
                groupsMap.forEach((group, groupUuid) => {
                    filteredGroups[groupUuid] = group;
                });

                filteredData[dateKey] = {
                    ...dayData,
                    propertyGroups: filteredGroups
                };
            }
        }
    });

    return filteredData;
};

/**
 * Calculate actual filtered group count after applying type filter
 * Works with both old structure (propertyGroups) and new structure (schedules)
 * @param {Object} dataToUse - Week data object with data property
 * @param {string|null} selectedDate - Selected date key or null for entire week
 * @param {Array} selectedGroups - Array of selected group UUIDs
 * @param {string} selectedType - Type filter: 'remote', 'nonRemote', or 'both'
 * @returns {number} Count of filtered groups
 */
export const getFilteredGroupCount = (dataToUse, selectedDate, selectedGroups, selectedType) => {
    let dataToFilter;

    if (selectedDate && dataToUse.data[selectedDate]) {
        dataToFilter = { [selectedDate]: dataToUse.data[selectedDate] };
    } else {
        dataToFilter = dataToUse.data;
    }

    const groupSet = new Set();

    Object.entries(dataToFilter).forEach(([dateKey, dayData]) => {
        // Old structure
        if (dayData.propertyGroups) {
            Object.entries(dayData.propertyGroups).forEach(([groupUuid, group]) => {
                if (selectedGroups.length > 0 && !selectedGroups.includes(groupUuid)) {
                    return;
                }

                let hasMatchingDisconnects = false;

                Object.entries(group.properties || {}).forEach(([propertyUuid, property]) => {
                    if (!property.disconnects) return;

                    let filteredDisconnects = property.disconnects;

                    if (selectedType === 'remote') {
                        filteredDisconnects = filteredDisconnects.filter(
                            d => d.isRemote === 1 || d.isRemote === true
                        );
                    } else if (selectedType === 'nonRemote') {
                        filteredDisconnects = filteredDisconnects.filter(
                            d => d.isRemote === 0 || d.isRemote === false || d.isRemote == null
                        );
                    }

                    if (filteredDisconnects.length > 0) {
                        hasMatchingDisconnects = true;
                    }
                });

                if (hasMatchingDisconnects) {
                    groupSet.add(groupUuid);
                }
            });
        }
        // New structure: count groups from schedules
        else if (dayData.schedules) {
            Object.values(dayData.schedules).forEach((schedule) => {
                const propertyGroup = schedule.propertyGroup;
                if (!propertyGroup) return;

                const groupUuid = propertyGroup.uuid;

                if (selectedGroups.length > 0 && !selectedGroups.includes(groupUuid)) {
                    return;
                }

                let hasMatchingAccounts = false;

                Object.values(schedule.properties || {}).forEach((property) => {
                    if (!property.propertyInfo || !property.accounts) return;

                    const isRemote = property.propertyInfo.isRemote === 1 || property.propertyInfo.isRemote === true;

                    // Filter by remote status
                    if (selectedType === 'remote' && !isRemote) return;
                    if (selectedType === 'nonRemote' && isRemote) return;

                    if (property.accounts.length > 0) {
                        hasMatchingAccounts = true;
                    }
                });

                if (hasMatchingAccounts) {
                    groupSet.add(groupUuid);
                }
            });
        }
    });

    return groupSet.size;
};

/**
 * Generate print filename based on selected options
 * @param {Object} dataToUse - Week data object with data property
 * @param {string|null} selectedDate - Selected date key or null for entire week
 * @param {string} selectedType - Type filter: 'remote', 'nonRemote', or 'both'
 * @param {Function} getWeekStartDate - Function to get week start date
 * @returns {string} Generated filename
 */
export const generatePrintFilename = (dataToUse, selectedDate, selectedType, getWeekStartDate) => {
    const printScope = selectedDate ? 'day' : 'week';
    let filename = 'Disconnect Schedule - ';

    if (printScope === 'day' && selectedDate) {
        const dayData = dataToUse.data[selectedDate];
        if (dayData) {
            // Handle date as string "2025-12-15" or ISO string
            const dateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
                ? dayData.date.split('T')[0]
                : dayData.date;
            const [year, month, day] = dateStr.split('-').map(Number);
            const date = new Date(year, month - 1, day, 12, 0, 0);
            filename += format(date, 'yyyy-MM-dd');
        }
    } else {
        const weekStart = getWeekStartDate();
        filename += format(weekStart, 'yyyy-MM-dd') + ' to ' + format(addDays(weekStart, 6), 'yyyy-MM-dd');
    }

    if (selectedType === 'remote') {
        filename += ' - Remote Only';
    } else if (selectedType === 'nonRemote') {
        filename += ' - Non-Remote Only';
    }

    return filename;
};

/**
 * Generate print content for a single group
 * Works with both old structure (disconnects) and new structure (accounts)
 * @param {Object} group - Property group object
 * @param {string} targetDateStr - Target date string in YYYY-MM-DD format
 * @param {string} type - Type: 'remote' or 'nonRemote'
 * @returns {string} HTML string for group print content
 */
export const generateGroupPrintContent = (group, targetDateStr, type) => {
    // Collect all properties with qualifying accounts
    const propertiesWithAccounts = [];

    Object.values(group.properties || {}).forEach((property) => {
        const accounts = getDisconnectsForDate(property, targetDateStr);
        // Filter by qualifying criteria for old structure (new structure already filtered)
        const qualifyingAccounts = property.disconnects
            ? filterQualifyingAccounts(accounts, targetDateStr)
            : accounts; // Already filtered in filterDataForPrint for new structure

        if (qualifyingAccounts.length > 0) {
            propertiesWithAccounts.push({
                ...property,
                accounts: qualifyingAccounts
            });
        }
    });

    // Skip groups with no qualifying accounts to avoid blank pages
    if (propertiesWithAccounts.length === 0) {
        return '';
    }

    let html = `<div class="print-group">`;
    html += `<h3 class="print-group-title">${group.groupName}</h3>`;

    propertiesWithAccounts.forEach((property) => {
        const accounts = property.accounts;

        html += `<div class="print-property">`;
        html += `<h4 class="print-property-title">Property: ${property.propertyName}</h4>`;
        html += `<p class="print-property-count">${accounts.length} ${accounts.length === 1 ? 'account' : 'accounts'}</p>`;

        html += `<table class="print-table">`;
        html += `<thead><tr>`;
        html += `<th>Account ID</th>`;
        html += `<th>Name</th>`;
        html += `<th>Account</th>`;
        html += `<th>Meter</th>`;
        html += `<th>Status</th>`;
        html += `<th>Address</th>`;
        html += `<th>Balance</th>`;
        html += `</tr></thead>`;
        html += `<tbody>`;

        accounts.forEach((account) => {
            html += `<tr>`;
            // Old structure: accountDisconnectId, New structure: account id or uuid
            html += `<td>${account.accountDisconnectId || account.accountId || account.accountUuid || ''}</td>`;
            html += `<td>${account.firstName || ''} ${account.lastName || ''}</td>`;
            html += `<td>${account.accountNum || ''}</td>`;
            html += `<td>${account.meterNum || ''}</td>`;
            html += `<td>${account.AccountStatus || 'Scheduled'}</td>`;
            // Address: old structure has address1/address2, new structure has unit.address1/address2
            const address = account.address1 || (account.unit && account.unit.address1) || '';
            const address2 = account.address2 || (account.unit && account.unit.address2) || '';
            const city = account.city || (account.unit && account.unit.city) || '';
            const state = account.state || (account.unit && account.unit.state) || '';
            const zip = account.zip || (account.unit && account.unit.zip) || '';
            html += `<td>${address} ${address2}, ${city}, ${state} ${zip}</td>`;
            // Balance: old structure has accountBalance, new structure might have accountBalance or pastDue
            const balance = account.accountBalance || account.pastDue || 0;
            html += `<td>$${typeof balance === 'number' ? balance.toFixed(2) : '0.00'}</td>`;
            html += `</tr>`;
        });

        html += `</tbody></table>`;
        html += `</div>`;
    });

    html += `</div>`;
    return html;
};

/**
 * Generate print-ready HTML content
 * @param {Object} filteredData - Filtered data object
 * @param {string} printScope - Print scope: 'day' or 'week'
 * @returns {string} HTML string for print content
 */
export const generatePrintContent = (filteredData, printScope) => {
    let html = '<div class="print-content">';

    // Check if filteredData is empty
    if (Object.keys(filteredData).length === 0) {
        html += '<div class="print-day-section">';
        html += '<h1 class="print-day-title">No Data Available</h1>';
        html += '<p>No disconnect schedules match the selected filters.</p>';
        html += '</div>';
        html += '</div>';
        return html;
    }

    const dayEntries = Object.entries(filteredData);
    const isSingleDay = dayEntries.length === 1;

    Object.entries(filteredData).forEach(([dateKey, dayData], index) => {
        // Handle date as string "2025-12-15" or ISO string
        const dateStr = typeof dayData.date === 'string' && dayData.date.includes('T')
            ? dayData.date.split('T')[0]
            : dayData.date;
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day, 12, 0, 0);
        const dateDisplay = {
            dayName: format(date, 'EEEE'),
            dayNumber: format(date, 'd'),
            monthYear: format(date, 'MMMM yyyy')
        };

        // Only add page-break-after for multi-day prints, not single day
        const daySectionClass = isSingleDay ? 'print-day-section print-day-section-single' : 'print-day-section';
        html += `<div class="${daySectionClass}">`;
        html += `<h1 class="print-day-title">${dateDisplay.dayName}, ${dateDisplay.monthYear} ${dateDisplay.dayNumber}</h1>`;

        const { remote, nonRemote } = organizeGroupsByRemoteStatus(dayData);

        // Generate content for remote groups and filter out empty ones
        const remoteContent = remote
            .map(group => generateGroupPrintContent(group, dateStr, 'remote'))
            .filter(content => content.length > 0);

        // Generate content for non-remote groups and filter out empty ones
        const nonRemoteContent = nonRemote
            .map(group => generateGroupPrintContent(group, dateStr, 'nonRemote'))
            .filter(content => content.length > 0);

        if (remoteContent.length > 0) {
            html += `<div class="print-section">`;
            html += `<h2 class="print-section-title">Remote Groups</h2>`;
            remoteContent.forEach((content) => {
                html += content;
            });
            html += `</div>`;
        }

        if (nonRemoteContent.length > 0) {
            html += `<div class="print-section">`;
            html += `<h2 class="print-section-title">Non-Remote Groups</h2>`;
            nonRemoteContent.forEach((content) => {
                html += content;
            });
            html += `</div>`;
        }

        html += `</div>`;
    });

    html += '</div>';
    return html;
};

/**
 * Execute print with filtered data
 * @param {Object} options - Print options
 * @param {Object} options.dataToUse - Week data object with data property
 * @param {string|null} options.selectedDate - Selected date key or null for entire week
 * @param {Array} options.selectedGroups - Array of selected group UUIDs
 * @param {string} options.selectedType - Type filter: 'remote', 'nonRemote', or 'both'
 * @param {Function} options.getWeekStartDate - Function to get week start date
 * @param {Function} options.onCloseDialog - Callback to close print dialog
 * @returns {void}
 */
export const executePrint = ({ dataToUse, selectedDate, selectedGroups, selectedType, getWeekStartDate, onCloseDialog }) => {
    const filteredData = filterDataForPrint(dataToUse, selectedDate, selectedGroups, selectedType);

    // Save original document title
    const originalTitle = document.title;

    // Set print filename as document title (browsers use this as default PDF filename)
    const printFilename = generatePrintFilename(dataToUse, selectedDate, selectedType, getWeekStartDate);
    document.title = printFilename;

    // Add print class to body to trigger print styles
    document.body.classList.add('printing');

    // Create a temporary container for print content
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.className = 'print-container';
    document.body.appendChild(printContainer);

    // Generate print content
    const printContent = generatePrintContent(filteredData, selectedDate ? 'day' : 'week');
    printContainer.innerHTML = printContent;

    // Close dialog if callback provided
    if (onCloseDialog) {
        onCloseDialog();
    }

    // Trigger print after a short delay to ensure content is rendered
    setTimeout(() => {
        window.print();

        // Clean up after printing
        setTimeout(() => {
            // Restore original document title
            document.title = originalTitle;
            document.body.classList.remove('printing');
            if (printContainer.parentNode) {
                printContainer.parentNode.removeChild(printContainer);
            }
        }, 100);
    }, 100);
};

/**
 * Print styles CSS string
 */
export const PRINT_STYLES = `
    @media print {
        /* Hide non-essential UI elements */
        .no-print,
        button:not(.print-button),
        .MuiToggleButtonGroup-root,
        .MuiAccordionSummary-root,
        .MuiIconButton-root,
        .MuiTooltip-root {
            display: none !important;
        }
        
        /* Expand all accordions */
        .MuiAccordion-root {
            box-shadow: none !important;
            border: 1px solid #ddd !important;
        }
        
        .MuiAccordionDetails-root {
            display: block !important;
        }
        
        /* Print container styles */
        body.printing {
            background: white !important;
        }
        
        #print-container {
            display: block !important;
        }
        
        .print-content {
            font-family: Arial, sans-serif;
            color: black;
        }
        
        .print-day-section {
            page-break-after: always;
            margin-bottom: 20px;
        }
        
        .print-day-section:last-child {
            page-break-after: auto;
        }
        
        .print-day-section-single {
            page-break-after: auto !important;
            orphans: 3;
            widows: 3;
        }
        
        .print-day-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
            color: black;
            page-break-after: avoid;
            orphans: 3;
            widows: 3;
        }
        
        .print-day-section .print-section:first-of-type {
            page-break-before: avoid;
            margin-top: 0;
        }
        
        .print-day-section-single .print-day-title + .print-section {
            page-break-before: avoid;
        }
        
        .print-section {
            margin-bottom: 20px;
        }
        
        .print-section-title {
            font-size: 18px;
            font-weight: bold;
            margin-top: 20px;
            margin-bottom: 10px;
            color: black;
            page-break-after: avoid;
        }
        
        .print-section .print-group:first-of-type {
            page-break-before: avoid;
        }
        
        .print-group {
            margin-bottom: 30px;
            page-break-inside: avoid;
        }
        
        .print-group-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            color: black;
        }
        
        .print-property {
            margin-bottom: 20px;
            page-break-inside: avoid;
        }
        
        .print-property-title {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
            color: black;
        }
        
        .print-property-count {
            font-size: 12px;
            color: #666;
            margin-bottom: 10px;
        }
        
        .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            page-break-inside: avoid;
        }
        
        .print-table th,
        .print-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 12px;
        }
        
        .print-table th {
            background-color: #f5f5f5;
            font-weight: bold;
        }
        
        .print-table tr {
            page-break-inside: avoid;
        }
        
        /* Hide the main content when printing */
        body.printing > *:not(#print-container) {
            display: none !important;
        }
        
        /* Show only print container */
        #print-container {
            display: block !important;
            width: 100%;
            padding: 20px;
        }
    }
`;

